import React, { useState, useEffect, useRef } from "react";

/* ═══════════════════════════════════════
   TRANSLATIONS
   ═══════════════════════════════════════ */
const t = {
  es: {
    nav_cta: "Agendar demo",
    hero_tag: "Gestión de reseñas con IA",
    hero_h1_1: "Tu reputación en",
    hero_h1_2: "piloto automático",
    hero_sub: "Repplio responde automáticamente a todas tus reseñas de Google, analiza el sentimiento por sucursal y te alerta al instante cuando algo sale mal.",
    hero_cta: "Empieza gratis — 30 días",
    hero_cta2: "Ver demo en 5 min",
    hero_proof: "+10,000 reseñas respondidas para Johnny Rockets México",
    problem_tag: "El problema",
    problem_h2: "Tus reseñas sin responder te están costando clientes",
    problem_cards: [
      { stat: "76%", desc: "de los consumidores leen reseñas de Google antes de visitar un restaurante" },
      { stat: "20-33 hrs", desc: "al mes se necesitan para responder manualmente las reseñas de 10 sucursales" },
      { stat: "$3,000-$5,000", desc: "es lo que cuesta en ingresos perdidos cada reseña negativa sin responder" },
    ],
    how_tag: "Cómo funciona",
    how_h2: "Conecta tu Google Business. Nosotros hacemos el resto.",
    how_steps: [
      { num: "01", title: "Conecta", desc: "Vincula tus perfiles de Google My Business. Funciona con cualquier número de sucursales." },
      { num: "02", title: "Personaliza", desc: "Define el tono de tu marca, idioma, reglas de escalación y datos de contacto para reseñas negativas." },
      { num: "03", title: "Activa", desc: "Repplio empieza a responder automáticamente cada 30 minutos. Tú solo monitoreas el dashboard." },
    ],
    features_tag: "Funcionalidades",
    features_h2: "Todo lo que necesitas para dominar tu reputación",
    features: [
      { icon: "💬", title: "Bot de respuestas con IA", desc: "Responde a cada reseña en el tono de tu marca, detectando el idioma automáticamente. Las reseñas negativas incluyen un mensaje empático con contacto de escalación. Modo automático o con aprobación previa." },
      { icon: "📊", title: "Dashboard de reputación", desc: "Panel en tiempo real con calificación promedio, volumen de reseñas, tasa de respuesta y tendencias por sucursal. Filtra por ubicación, fecha y categoría de sentimiento." },
      { icon: "🧠", title: "Análisis de sentimiento", desc: "La IA clasifica cada reseña por temas: calidad de comida, velocidad de servicio, limpieza, atención del personal, delivery y más. Resúmenes ejecutivos semanales por sucursal." },
      { icon: "🚨", title: "Alertas instantáneas", desc: 'Notificación inmediata por email o WhatsApp cuando recibes una reseña de 1-2 estrellas o con palabras clave críticas como "intoxicación" o "insectos".' },
    ],
    demo_tag: "Demo en vivo",
    demo_h2: "Mira cómo la IA responde a una reseña real",
    demo_review_name: "María García",
    demo_review_date: "Hace 2 días",
    demo_review_text: "La comida tardó mucho en llegar y las papas estaban frías. El mesero fue amable pero el servicio necesita mejorar. No creo que vuelva pronto.",
    demo_reply_label: "Respuesta de Repplio IA — generada en 3 segundos:",
    demo_reply_text: "Hola María, muchas gracias por tomarte el tiempo de compartirnos tu experiencia. Lamentamos que los tiempos de espera y la temperatura de las papas no cumplieron con nuestros estándares — ese no es el nivel de servicio que buscamos ofrecer. Tu comentario es muy valioso y ya lo compartimos con el equipo de esta sucursal para tomar acción inmediata. Si deseas compartirnos más detalles, escríbenos a soporte@restaurante.mx. ¡Esperamos poder darte una mejor experiencia pronto!",
    case_tag: "Caso de éxito",
    case_h2: "Resultados reales en Johnny Rockets México",
    case_desc: "Virko Group opera 7+ sucursales de Johnny Rockets y Nathan's Famous en México. Antes de Repplio, tenían más de 10,000 reseñas de Google sin responder acumuladas durante años.",
    case_stats: [
      { value: "10,000+", label: "reseñas respondidas" },
      { value: "<48 hrs", label: "para limpiar el backlog" },
      { value: "0→100%", label: "tasa de respuesta" },
      { value: "<30 min", label: "tiempo de respuesta" },
    ],
    case_quote: "\"El sistema corre solo. No hemos tocado una reseña manualmente en meses y nuestra calificación promedio ha subido consistentemente.\"",
    case_author: "— Operador, Virko Group",
    pricing_tag: "Precio",
    pricing_h2: "Simple. Por sucursal. Sin sorpresas.",
    pricing_price: "$29",
    pricing_unit: "USD / sucursal / mes",
    pricing_includes: "Todo incluido:",
    pricing_features: [
      "Respuestas ilimitadas con IA",
      "Dashboard de reputación completo",
      "Análisis de sentimiento semanal",
      "Alertas por email y WhatsApp",
      "Soporte en español",
      "Configuración incluida — nosotros lo activamos",
    ],
    pricing_cta: "Empieza con piloto gratuito de 30 días",
    pricing_note: "Sin tarjeta de crédito. Sin contratos. Cancela cuando quieras.",
    pricing_enterprise: "¿Más de 15 sucursales? ",
    pricing_enterprise_link: "Contáctanos para precio enterprise →",
    final_h2: "¿Listo para poner tu reputación en piloto automático?",
    final_sub: "Únete a los primeros operadores que están transformando cómo gestionan sus reseñas de Google.",
    final_cta: "Agendar demo de 15 minutos",
    final_cta2: "Escríbenos por WhatsApp →",
    footer_built: "Desarrollado por",
    footer_from: "Hecho con ❤️ desde México y Madrid",
  },
  en: {
    nav_cta: "Book a demo",
    hero_tag: "AI-powered review management",
    hero_h1_1: "Your reputation",
    hero_h1_2: "on autopilot",
    hero_sub: "Repplio automatically replies to all your Google reviews, analyzes sentiment by location, and alerts you instantly when something goes wrong.",
    hero_cta: "Start free — 30 day pilot",
    hero_cta2: "Watch 5-min demo",
    hero_proof: "10,000+ reviews answered for Johnny Rockets Mexico",
    problem_tag: "The problem",
    problem_h2: "Your unanswered reviews are costing you customers",
    problem_cards: [
      { stat: "76%", desc: "of consumers read Google reviews before visiting a restaurant" },
      { stat: "20-33 hrs", desc: "per month needed to manually respond to reviews across 10 locations" },
      { stat: "$3,000-$5,000", desc: "in lost revenue per unanswered negative review" },
    ],
    how_tag: "How it works",
    how_h2: "Connect your Google Business. We handle the rest.",
    how_steps: [
      { num: "01", title: "Connect", desc: "Link your Google My Business profiles. Works with any number of locations." },
      { num: "02", title: "Customize", desc: "Set your brand tone, language, escalation rules, and contact details for negative reviews." },
      { num: "03", title: "Activate", desc: "Repplio starts replying automatically every 30 minutes. You just monitor the dashboard." },
    ],
    features_tag: "Features",
    features_h2: "Everything you need to own your reputation",
    features: [
      { icon: "💬", title: "AI reply bot", desc: "Responds to every review in your brand voice, auto-detecting the language. Negative reviews get empathetic replies with escalation contacts. Auto-publish or queue for approval." },
      { icon: "📊", title: "Reputation dashboard", desc: "Real-time panel with average rating, review volume, response rate, and trends by location. Filter by location, date range, and sentiment category." },
      { icon: "🧠", title: "Sentiment analysis", desc: "AI classifies every review by theme: food quality, service speed, cleanliness, staff friendliness, delivery and more. Weekly executive summaries per location." },
      { icon: "🚨", title: "Instant alerts", desc: 'Immediate notification via email or WhatsApp when you receive a 1-2 star review or critical keywords like "food poisoning" or "insects" are detected.' },
    ],
    demo_tag: "Live demo",
    demo_h2: "Watch the AI reply to a real review",
    demo_review_name: "María García",
    demo_review_date: "2 days ago",
    demo_review_text: "The food took forever to arrive and the fries were cold. The waiter was nice but the service needs improvement. I don't think I'll be back anytime soon.",
    demo_reply_label: "Repplio AI reply — generated in 3 seconds:",
    demo_reply_text: "Hi María, thank you so much for taking the time to share your experience. We're sorry to hear the wait times and food temperature didn't meet our standards — that's not the level of service we aim to provide. Your feedback is incredibly valuable and we've already shared it with the team at this location for immediate action. If you'd like to share more details, reach out to us at support@restaurant.mx. We hope to give you a much better experience next time!",
    case_tag: "Case study",
    case_h2: "Real results at Johnny Rockets Mexico",
    case_desc: "Virko Group operates 7+ Johnny Rockets and Nathan's Famous locations across Mexico. Before Repplio, they had over 10,000 unanswered Google reviews accumulated over years.",
    case_stats: [
      { value: "10,000+", label: "reviews answered" },
      { value: "<48 hrs", label: "to clear the backlog" },
      { value: "0→100%", label: "response rate" },
      { value: "<30 min", label: "response time" },
    ],
    case_quote: "\"The system runs itself. We haven't manually touched a review in months and our average rating has consistently improved.\"",
    case_author: "— Operator, Virko Group",
    pricing_tag: "Pricing",
    pricing_h2: "Simple. Per location. No surprises.",
    pricing_price: "$29",
    pricing_unit: "USD / location / month",
    pricing_includes: "Everything included:",
    pricing_features: [
      "Unlimited AI replies",
      "Full reputation dashboard",
      "Weekly sentiment analysis",
      "Email and WhatsApp alerts",
      "Spanish & English support",
      "Setup included — we activate it for you",
    ],
    pricing_cta: "Start with a free 30-day pilot",
    pricing_note: "No credit card. No contracts. Cancel anytime.",
    pricing_enterprise: "More than 15 locations? ",
    pricing_enterprise_link: "Contact us for enterprise pricing →",
    final_h2: "Ready to put your reputation on autopilot?",
    final_sub: "Join the first operators transforming how they manage Google reviews.",
    final_cta: "Book a 15-minute demo",
    final_cta2: "Message us on WhatsApp →",
    footer_built: "Built by",
    footer_from: "Made with ❤️ from Mexico & Madrid",
  }
};

/* ═══════════════════════════════════════
   CONFIG — UPDATE THESE WITH YOUR REAL LINKS
   ═══════════════════════════════════════ */
const CONFIG = {
  CALENDLY_URL: "https://calendar.app.google/7WHxS9it5JqY9hTT8",       // ← Replace with your Calendly
  WHATSAPP_URL: "https://wa.me/34641387277?text=Hola%2C%20me%20interesa%20Repplio", // ← Replace with your number
  LOOM_EMBED: "",  // ← Paste your Loom embed URL here when ready
};

/* ═══════════════════════════════════════
   SVG LOGO COMPONENT (pure vector paths, no font dependency)
   ═══════════════════════════════════════ */
function RepplioLogo({ color = "#1A73C7", height = 28 }) {
  const scale = height / 56;
  return (
    <svg viewBox="0 0 168 56" height={height} width={168 * scale} xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(8.0, 46.0) scale(0.0420, -0.0420)">
        <path d="M408 564V383H361Q297 383 265.0 355.5Q233 328 233 259V0H62V558H233V465Q263 511 308.0 537.5Q353 564 408 564Z" fill={color} stroke={color} strokeWidth="40" strokeLinejoin="round"/>
      </g>
      <g transform="translate(26.0, 46.0) scale(0.0420, -0.0420)">
        <path d="M585 238H198Q202 186 231.5 158.5Q261 131 304 131Q368 131 393 185H575Q561 130 524.5 86.0Q488 42 433.0 17.0Q378 -8 310 -8Q228 -8 164.0 27.0Q100 62 64.0 127.0Q28 192 28 279Q28 366 63.5 431.0Q99 496 163.0 531.0Q227 566 310 566Q391 566 454.0 532.0Q517 498 552.5 435.0Q588 372 588 288Q588 264 585 238ZM413 333Q413 377 383.0 403.0Q353 429 308 429Q265 429 235.5 404.0Q206 379 199 333Z" fill={color} stroke={color} strokeWidth="40" strokeLinejoin="round"/>
      </g>
      <g transform="translate(51.8, 46.0) scale(0.0420, -0.0420)">
        <path d="M405 566Q474 566 530.0 531.0Q586 496 618.5 431.0Q651 366 651 280Q651 194 618.5 128.5Q586 63 530.0 27.5Q474 -8 405 -8Q347 -8 302.5 16.0Q258 40 233 78V-266H62V558H233V479Q258 518 302.0 542.0Q346 566 405 566ZM354 417Q303 417 267.5 380.0Q232 343 232 279Q232 215 267.5 178.0Q303 141 354 141Q405 141 441.0 178.5Q477 216 477 280Q477 344 441.5 380.5Q406 417 354 417Z" fill={color} stroke={color} strokeWidth="40" strokeLinejoin="round"/>
      </g>
      <g transform="translate(80.4, 46.0) scale(0.0420, -0.0420)">
        <path d="M405 566Q474 566 530.0 531.0Q586 496 618.5 431.0Q651 366 651 280Q651 194 618.5 128.5Q586 63 530.0 27.5Q474 -8 405 -8Q347 -8 302.5 16.0Q258 40 233 78V-266H62V558H233V479Q258 518 302.0 542.0Q346 566 405 566ZM354 417Q303 417 267.5 380.0Q232 343 232 279Q232 215 267.5 178.0Q303 141 354 141Q405 141 441.0 178.5Q477 216 477 280Q477 344 441.5 380.5Q406 417 354 417Z" fill={color} stroke={color} strokeWidth="40" strokeLinejoin="round"/>
      </g>
      <g transform="translate(108.9, 46.0) scale(0.0420, -0.0420)">
        <path d="M233 740V0H62V740Z" fill={color} stroke={color} strokeWidth="40" strokeLinejoin="round"/>
      </g>
      <g transform="translate(121.3, 46.0) scale(0.0420, -0.0420)">
        <path d="M46 708Q46 748 74.5 774.5Q103 801 148 801Q192 801 220.5 774.5Q249 748 249 708Q249 669 220.5 642.5Q192 616 148 616Q103 616 74.5 642.5Q46 669 46 708ZM233 558V0H62V558Z" fill={color} stroke={color} strokeWidth="40" strokeLinejoin="round"/>
      </g>
      <g transform="translate(133.7, 46.0) scale(0.0420, -0.0420)">
        <path d="M28 279Q28 365 66.0 430.5Q104 496 170.0 531.0Q236 566 318 566Q400 566 466.0 531.0Q532 496 570.0 430.5Q608 365 608 279Q608 193 569.5 127.5Q531 62 464.5 27.0Q398 -8 316 -8Q234 -8 168.5 27.0Q103 62 65.5 127.0Q28 192 28 279ZM434 279Q434 346 400.5 382.0Q367 418 318 418Q268 418 235.0 382.5Q202 347 202 279Q202 212 234.5 176.0Q267 140 316 140Q365 140 399.5 176.0Q434 212 434 279Z" fill={color} stroke={color} strokeWidth="40" strokeLinejoin="round"/>
      </g>
    </svg>
  );
}

/* ═══════════════════════════════════════
   COMPONENTS
   ═══════════════════════════════════════ */
function StarRating({ stars }) {
  return (
    <div style={{ display: "flex", gap: 2 }}>
      {[1,2,3,4,5].map(i => (
        <svg key={i} width="18" height="18" viewBox="0 0 20 20">
          <path d="M10 1.5l2.47 5.01 5.53.8-4 3.9.94 5.49L10 14.27 5.06 16.7l.94-5.49-4-3.9 5.53-.8z"
            fill={i <= stars ? "#F5A623" : "#DDD"} stroke="none"/>
        </svg>
      ))}
    </div>
  );
}

function TypewriterText({ text, active }) {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!active) { setDisplayed(""); setDone(false); return; }
    let i = 0; setDisplayed(""); setDone(false);
    const interval = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) { clearInterval(interval); setDone(true); }
    }, 12);
    return () => clearInterval(interval);
  }, [active, text]);
  return <span>{displayed}{active && !done && <span style={{ borderRight: "2px solid #1A73C7", marginLeft: 1, animation: "blink 0.8s infinite" }}>&nbsp;</span>}</span>;
}

function FadeIn({ children, delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.15 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(24px)",
      transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
    }}>
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════
   STYLES
   ═══════════════════════════════════════ */
const css = {
  section: { maxWidth: 960, margin: "0 auto", padding: "80px 24px" },
  sectionSm: { maxWidth: 960, margin: "0 auto", padding: "60px 24px" },
  tag: { fontSize: 12, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase", color: "#1A73C7", marginBottom: 12 },
  h2: { fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: "clamp(26px, 4vw, 36px)", fontWeight: 800, color: "#1C1C1C", lineHeight: 1.15, marginBottom: 16, letterSpacing: -0.5 },
  sub: { fontSize: 17, color: "#555", lineHeight: 1.7, maxWidth: 600 },
  btn: { background: "#1A73C7", color: "white", border: "none", borderRadius: 10, padding: "14px 32px", fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", textDecoration: "none", display: "inline-block", textAlign: "center" },
  btnOutline: { background: "none", color: "#1A73C7", border: "2px solid #1A73C7", borderRadius: 10, padding: "12px 28px", fontSize: 16, fontWeight: 600, cursor: "pointer", textDecoration: "none", display: "inline-block", textAlign: "center" },
  card: { background: "white", borderRadius: 12, padding: "28px 24px", border: "1px solid #EAEAEA" },
};

/* ═══════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════ */
export default function App() {
  const [lang, setLang] = useState("es");
  const [demoActive, setDemoActive] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const demoRef = useRef(null);
  const c = t[lang];

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setDemoActive(true);
    }, { threshold: 0.3 });
    if (demoRef.current) observer.observe(demoRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenu(false);
  };

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", color: "#1C1C1C", background: "white" }}>
      <style>{`
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @media (max-width: 768px) {
          .grid-3 { grid-template-columns: 1fr !important; }
          .grid-2 { grid-template-columns: 1fr !important; }
          .grid-4 { grid-template-columns: 1fr 1fr !important; }
          .steps-row { flex-direction: column !important; }
          .step-line { display: none !important; }
          .hero-buttons { flex-direction: column !important; align-items: center !important; }
          .hero-h1 { font-size: 36px !important; }
        }
        @media (max-width: 480px) {
          .grid-4 { grid-template-columns: 1fr !important; }
        }
        a { color: inherit; }
      `}</style>

      {/* ═══ NAV ═══ */}
      <nav style={{ position: "sticky", top: 0, background: "rgba(255,255,255,0.95)", backdropFilter: "blur(12px)", zIndex: 100, borderBottom: "1px solid #F0F0F0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 24px", maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ cursor: "pointer", display: "flex", alignItems: "center" }} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <RepplioLogo color="#1A73C7" height={28} />
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button onClick={() => setLang(lang === "es" ? "en" : "es")} style={{
              background: "none", border: "1px solid #DDD", borderRadius: 6, padding: "6px 12px",
              fontSize: 13, cursor: "pointer", color: "#555", fontWeight: 500
            }}>
              {lang === "es" ? "EN 🇺🇸" : "ES 🇲🇽"}
            </button>
            <a href={CONFIG.CALENDLY_URL} target="_blank" rel="noopener noreferrer" style={{ ...css.btn, padding: "10px 20px", fontSize: 14 }}>
              {c.nav_cta}
            </a>
          </div>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <div style={{ ...css.section, paddingTop: 60, paddingBottom: 60, textAlign: "center" }}>
        <FadeIn>
          <div style={css.tag}>{c.hero_tag}</div>
          <h1 className="hero-h1" style={{
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            fontSize: "clamp(36px, 5.5vw, 56px)", fontWeight: 800,
            lineHeight: 1.08, letterSpacing: -1, color: "#1C1C1C", marginBottom: 20
          }}>
            {c.hero_h1_1}<br/>{c.hero_h1_2}
          </h1>
          <p style={{ ...css.sub, margin: "0 auto 32px", maxWidth: 560, fontSize: 18 }}>
            {c.hero_sub}
          </p>
          <div className="hero-buttons" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginBottom: 24 }}>
            <a href={CONFIG.CALENDLY_URL} target="_blank" rel="noopener noreferrer" style={css.btn}>
              {c.hero_cta}
            </a>
            <button onClick={() => scrollTo("demo")} style={css.btnOutline}>
              ▶ {c.hero_cta2}
            </button>
          </div>
          <p style={{ fontSize: 13, color: "#888" }}>{c.hero_proof}</p>
        </FadeIn>
      </div>

      {/* ═══ PROBLEM ═══ */}
      <div style={{ background: "#F8F9FB" }}>
        <div style={css.section}>
          <FadeIn>
            <div style={css.tag}>{c.problem_tag}</div>
            <h2 style={css.h2}>{c.problem_h2}</h2>
          </FadeIn>
          <div className="grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginTop: 32 }}>
            {c.problem_cards.map((card, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div style={css.card}>
                  <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 800, color: "#1A73C7", marginBottom: 8 }}>{card.stat}</div>
                  <p style={{ fontSize: 14, color: "#555", lineHeight: 1.6 }}>{card.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ HOW IT WORKS ═══ */}
      <div style={css.section}>
        <FadeIn>
          <div style={css.tag}>{c.how_tag}</div>
          <h2 style={css.h2}>{c.how_h2}</h2>
        </FadeIn>
        <div className="steps-row" style={{ display: "flex", gap: 0, marginTop: 32, position: "relative" }}>
          {c.how_steps.map((step, i) => (
            <FadeIn key={i} delay={i * 0.15}>
              <div style={{ flex: 1, padding: "0 20px", position: "relative", textAlign: "center" }}>
                <div style={{
                  width: 56, height: 56, borderRadius: "50%", background: "#E8F2FC",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 16px",
                  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  fontSize: 18, fontWeight: 800, color: "#1A73C7"
                }}>{step.num}</div>
                {i < 2 && <div className="step-line" style={{
                  position: "absolute", top: 28, left: "calc(50% + 36px)", width: "calc(100% - 72px)",
                  height: 2, background: "#E0E0E0"
                }}/>}
                <h3 style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 18, fontWeight: 700, marginBottom: 8, color: "#1C1C1C" }}>{step.title}</h3>
                <p style={{ fontSize: 14, color: "#666", lineHeight: 1.6 }}>{step.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>

      {/* ═══ FEATURES ═══ */}
      <div style={{ background: "#F8F9FB" }}>
        <div style={css.section}>
          <FadeIn>
            <div style={css.tag}>{c.features_tag}</div>
            <h2 style={css.h2}>{c.features_h2}</h2>
          </FadeIn>
          <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 32 }}>
            {c.features.map((f, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <div style={{ ...css.card, height: "100%" }}>
                  <div style={{ fontSize: 28, marginBottom: 12 }}>{f.icon}</div>
                  <h3 style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 17, fontWeight: 700, marginBottom: 8, color: "#1C1C1C" }}>{f.title}</h3>
                  <p style={{ fontSize: 14, color: "#555", lineHeight: 1.7 }}>{f.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ DEMO ═══ */}
      <div id="demo" style={css.section} ref={demoRef}>
        <FadeIn>
          <div style={css.tag}>{c.demo_tag}</div>
          <h2 style={css.h2}>{c.demo_h2}</h2>
        </FadeIn>
        <div style={{ marginTop: 32, maxWidth: 640 }}>
          <FadeIn>
            <div style={{ ...css.card, background: "#F8F9FB", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: "50%", background: "#E8D4F0",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: 16, color: "#7D3C98"
                }}>M</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{c.demo_review_name}</div>
                  <div style={{ fontSize: 12, color: "#888" }}>{c.demo_review_date}</div>
                </div>
              </div>
              <StarRating stars={2} />
              <p style={{ fontSize: 14, color: "#444", lineHeight: 1.7, marginTop: 10 }}>{c.demo_review_text}</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div style={{
              background: "#E8F2FC", borderRadius: 12, padding: "24px",
              border: "2px solid #1A73C7", position: "relative"
            }}>
              <div style={{
                position: "absolute", top: -10, left: 24, background: "#1A73C7",
                color: "white", fontSize: 11, fontWeight: 600, padding: "3px 10px",
                borderRadius: 4, letterSpacing: 0.5
              }}>REPPLIO AI</div>
              <p style={{ fontSize: 12, color: "#1A73C7", fontWeight: 600, marginBottom: 8 }}>{c.demo_reply_label}</p>
              <p style={{ fontSize: 14, color: "#0B4F8A", lineHeight: 1.7 }}>
                <TypewriterText text={c.demo_reply_text} active={demoActive} />
              </p>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* ═══ CASE STUDY ═══ */}
      <div style={{ background: "#0B4F8A" }}>
        <div style={{ ...css.section, color: "white" }}>
          <FadeIn>
            <div style={{ ...css.tag, color: "#7EB8E8" }}>{c.case_tag}</div>
            <h2 style={{ ...css.h2, color: "white" }}>{c.case_h2}</h2>
            <p style={{ fontSize: 16, color: "#B8D8F0", lineHeight: 1.7, marginBottom: 32, maxWidth: 600 }}>{c.case_desc}</p>
          </FadeIn>
          <div className="grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 32 }}>
            {c.case_stats.map((s, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div style={{ background: "rgba(255,255,255,0.1)", borderRadius: 10, padding: "24px 16px", textAlign: "center" }}>
                  <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: "clamp(20px, 3vw, 26px)", fontWeight: 800, color: "#F5A623", marginBottom: 6 }}>{s.value}</div>
                  <div style={{ fontSize: 13, color: "#B8D8F0" }}>{s.label}</div>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn>
            <div style={{ borderLeft: "3px solid #F5A623", paddingLeft: 20, maxWidth: 560 }}>
              <p style={{ fontSize: 16, fontStyle: "italic", color: "#D4E8F7", lineHeight: 1.7 }}>{c.case_quote}</p>
              <p style={{ fontSize: 13, color: "#7EB8E8", marginTop: 8 }}>{c.case_author}</p>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* ═══ PRICING ═══ */}
      <div id="pricing" style={css.section}>
        <FadeIn>
          <div style={{ textAlign: "center" }}>
            <div style={css.tag}>{c.pricing_tag}</div>
            <h2 style={css.h2}>{c.pricing_h2}</h2>
          </div>
          <div style={{
            maxWidth: 420, margin: "40px auto 0", background: "white",
            borderRadius: 16, border: "2px solid #1A73C7", padding: "40px 32px", textAlign: "center"
          }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 4, marginBottom: 4 }}>
              <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 52, fontWeight: 800, color: "#1A73C7" }}>{c.pricing_price}</span>
            </div>
            <p style={{ fontSize: 15, color: "#888", marginBottom: 28 }}>{c.pricing_unit}</p>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#1C1C1C", marginBottom: 12, textAlign: "left" }}>{c.pricing_includes}</p>
            <div style={{ textAlign: "left", marginBottom: 28 }}>
              {c.pricing_features.map((f, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
                  <span style={{ color: "#0EAD69", fontWeight: 700, fontSize: 16, lineHeight: 1.3 }}>✓</span>
                  <span style={{ fontSize: 14, color: "#444", lineHeight: 1.5 }}>{f}</span>
                </div>
              ))}
            </div>
            <a href={CONFIG.CALENDLY_URL} target="_blank" rel="noopener noreferrer" style={{ ...css.btn, width: "100%", display: "block" }}>
              {c.pricing_cta}
            </a>
            <p style={{ fontSize: 12, color: "#999", marginTop: 12 }}>{c.pricing_note}</p>
            <p style={{ fontSize: 13, color: "#555", marginTop: 16 }}>
              {c.pricing_enterprise}
              <a href={CONFIG.WHATSAPP_URL} target="_blank" rel="noopener noreferrer" style={{ color: "#1A73C7", fontWeight: 600 }}>{c.pricing_enterprise_link}</a>
            </p>
          </div>
        </FadeIn>
      </div>

      {/* ═══ FINAL CTA ═══ */}
      <div style={{ background: "#1A73C7" }}>
        <div style={{ ...css.sectionSm, textAlign: "center", color: "white" }}>
          <FadeIn>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: "clamp(24px, 4vw, 32px)", fontWeight: 800, lineHeight: 1.2, letterSpacing: -0.5, marginBottom: 12, color: "white" }}>
              {c.final_h2}
            </h2>
            <p style={{ fontSize: 17, color: "#B8D8F0", marginBottom: 32, maxWidth: 500, margin: "0 auto 32px" }}>{c.final_sub}</p>
            <div className="hero-buttons" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <a href={CONFIG.CALENDLY_URL} target="_blank" rel="noopener noreferrer" style={{ ...css.btn, background: "white", color: "#1A73C7" }}>
                {c.final_cta}
              </a>
              <a href={CONFIG.WHATSAPP_URL} target="_blank" rel="noopener noreferrer" style={{ ...css.btnOutline, color: "white", borderColor: "rgba(255,255,255,0.4)" }}>
                {c.final_cta2}
              </a>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* ═══ FOOTER ═══ */}
      <div style={{ padding: "32px 24px", textAlign: "center", fontSize: 13, color: "#999" }}>
        <div style={{ marginBottom: 8, display: "flex", justifyContent: "center" }}><RepplioLogo color="#1A73C7" height={22} /></div>
        <p>{c.footer_built} <span style={{ fontWeight: 600, color: "#666" }}>Virko Group</span></p>
        <p style={{ marginTop: 4 }}>{c.footer_from}</p>
        <p style={{ marginTop: 8, fontSize: 12 }}>© 2026 Repplio. All rights reserved.</p>
      </div>
    </div>
  );
}
